import { Box, Grid } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { COLOR_GRAY_3 } from "../atoms/ImsMaterialTheme";
import ReceiptMenu from "./ReceiptMenu";
import CustomerNameCell from "../customerCare/CustomerNameCell";
import { formatEuro, formatUtcToLocalDateTimeInGermanFormat } from "../atoms/Utils";
import { Api } from "../../api/Api";
import { ReceiptSearchCondition } from "./ReceiptSearch";
import { IStore } from "../../store/IStore";
import { bindActionCreators, Dispatch } from "redux";
import { thunkCreateErrorNotification, thunkCreateSuccessNotification } from "../../store/actions/NotificationActions";
import { connect, ConnectedProps } from "react-redux";
import { ReceiptDto } from "../../store/models/receipt/ReceiptDto";

interface ReceiptTableProps {
  searchCondition: ReceiptSearchCondition;
}

const DEFAULT_SORT_FIELD = "receiptDate";
const DEFAULT_SORT_DIRECTION = "desc";

const ReceiptTable = (props: ReceiptTableProps & ThunkProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(10);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [receipts, setReceipts] = useState<ReceiptDto[]>([]);

  const fetchReceipts = useCallback(() => {
    setIsLoading(true);
    Api.getAllReceipts(page + 1, size, sortBy, sortDirection, props.searchCondition.status)
      .then((data) => {
        setReceipts(data.receipts);
        setTotal(data.total);
      })
      .catch((e) => {
        setReceipts([]);
        props.thunkCreateErrorNotification(t("receiptOverview.errorLoading"), e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props, t, page, size, sortBy, sortDirection]);

  useEffect(() => {
    fetchReceipts();
  }, [props, t, page, size, sortBy, sortDirection, fetchReceipts]);

  const resetSort = () => {
    setSortBy(DEFAULT_SORT_FIELD);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  };

  // Handle sorting changes
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length > 0) {
      const { field, sort } = newSortModel[0];
      setSortBy(field); // Set sort field
      setSortDirection(sort ?? DEFAULT_SORT_DIRECTION);
    } else {
      resetSort();
    }
  };

  const rows = receipts.map((receipt) => ({
    receiptNumber: receipt.receiptNumber,
    externalReceiptId: receipt.externalReceiptId,
    thumbnailUrl: receipt.thumbnailUrl,
    imageUrl: receipt.imageUrl,
    customerNumber: receipt.customerNumber,
    creationTimestamp: receipt.submissionDate,
    totalAmount: receipt.totalAmount,
    status: receipt.status,
  }));

  const columns: GridColDef[] = [
    {
      field: "creationTimestamp",
      headerName: t("receiptOverview.table.receiptDate"),
      disableColumnMenu: true,
      flex: 1,
      maxWidth: 220,
      sortable: true,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return null;
        try {
          return formatUtcToLocalDateTimeInGermanFormat(params.value);
        } catch {
          return null;
        }
      },
    },
    {
      field: "customerNumber",
      headerName: t("receiptOverview.table.customerNumber"),
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
      renderCell: (params) => <CustomerNameCell customerNumber={params.value} />,
    },
    {
      field: "externalReceiptId",
      headerName: t("receiptOverview.table.receiptNumber"),
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
    },

    {
      field: "totalAmount",
      headerName: t("receiptOverview.table.totalAmount"),
      flex: 1,
      maxWidth: 150,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (params.value ? formatEuro(params.value) : null),
    },
    {
      field: "status",
      headerName: t("receiptOverview.table.status"),
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => t(`receiptStatus.${params.value}`) || params.value,
    },
    {
      field: "thumbnailUrl",
      headerName: t("receiptOverview.table.image"),
      flex: 1,
      maxWidth: 120,
      renderCell: (params) => {
        // Use thumbnailUrl if available, otherwise fall back to imageUrl
        const imageSource = params.value || params.row.imageUrl;
        
        return imageSource ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: 70, height: 70, borderRadius: 5 }}>
            <img
              src={imageSource}
              alt={t("receiptOverview.table.imageAlt") || "receipt"}
              style={{ margin: 4, borderRadius: 4, objectFit: "cover", width: "100%", height: "100%" }}
            />
          </div>
        ) : null;
      },
      disableColumnMenu: true,
      sortable: false,
      editable: false,
      filterable: false,
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 50,
      disableExport: true,
      renderHeader: () => <span></span>,
      renderCell: (params) => <ReceiptMenu receipt={params.row} onReceiptUpdated={fetchReceipts} />,
    },
  ];

  return (
    <Grid>
      <Box
        sx={{
          width: "100%",
          bgcolor: "white",
          boxShadow: 2,
          borderRadius: 2,
          marginBottom: 4,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: COLOR_GRAY_3, // Light gray background
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold", // Makes header text bold
          },
        }}
      >
        <DataGrid
          disableColumnSelector={true}
          disableSelectionOnClick={true}
          disableVirtualization={true}
          getRowId={(row) => row.receiptNumber}
          rows={rows}
          rowCount={total}
          loading={isLoading}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          rowsPerPageOptions={[10, 50, 100]}
          pagination
          page={page}
          pageSize={size}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setSize(newPageSize)}
          columns={columns}
          autoHeight
          rowHeight={80}
          componentsProps={{
            pagination: {
              labelRowsPerPage: t("common.pagination.rowsPerPage"),
            },
          }}
        />
      </Box>
    </Grid>
  );
};

const mapStateToProps = (state: IStore) => ({});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkCreateErrorNotification,
      thunkCreateSuccessNotification,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(ReceiptTable);
